// Content Studio shapes returned by app/api/content.py. The layout schema mirrors
// app/services/content.py (Layout and its layers): the server validates every save against it.

export type ContentStatus = 'idea' | 'draft' | 'needs_review' | 'approved' | 'exported' | 'published' | 'archived';
export type ContentFormat = 'single' | 'poster' | 'carousel' | 'before_after';
export type UsageStatus = 'unreviewed' | 'approved' | 'restricted' | 'do_not_use';
export type Permission = 'unknown' | 'not_allowed' | 'allowed' | 'allowed_anonymous';
export type StyleName = 'title' | 'subtitle' | 'body' | 'caption' | 'label';

interface BoxBase {
  id: string;
  name?: string | null;
  x: number;
  y: number;
  w: number;
  h: number;
  hidden?: boolean;
  /** carousel only: also drawn on the other slides, shifted by whole slide widths */
  span?: boolean;
}
export interface Crop {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface ImageLayer extends BoxBase {
  type: 'image';
  asset_id: string | null;
  fit: 'fill' | 'fit';
  focal_x: number;
  focal_y: number;
  crop: Crop;
  slot?: number | null;
}
export interface TextLayer extends BoxBase {
  type: 'text';
  text: string;
  style: StyleName;
  color: string;
  align: 'left' | 'center' | 'right';
  valign: 'top' | 'middle' | 'bottom';
  size?: number | null;
  font?: string | null;
  weight?: number | null;
  bind?: string | null;
}
export interface LogoLayer extends BoxBase {
  type: 'logo';
  /** a brand logo version (its asset id); null/absent = the brand default */
  logo_id?: string | null;
}
export interface ShapeLayer extends BoxBase {
  type: 'shape';
  shape: 'rect' | 'square' | 'line' | 'arrow';
  color: string;
}
export type Layer = ImageLayer | TextLayer | LogoLayer | ShapeLayer;

export interface Artboard {
  id: string;
  name?: string | null;
  background: string;
  layers: Layer[];
}
export interface Caption {
  text: string;
  hashtags: string[];
}
export interface LayoutMeta {
  language: string;
  captions: Record<string, Caption>;
  alt_text: string[];
  credits: string | null;
}
export interface Layout {
  schema_version: 1;
  width: number;
  height: number;
  safe_margin_pct: number;
  artboards: Artboard[];
  meta: LayoutMeta;
}

export interface Style {
  font: string;
  weight: number;
  size_pct: number;
  line_height: number;
  uppercase: boolean;
}
export interface BrandSpec {
  colors: { name: string; hex: string }[];
  styles: Record<StyleName, Style>;
  safe_margin_pct: number;
}

export interface Asset {
  id: string;
  project_id: string | null;
  kind: 'image' | 'logo';
  sha256: string;
  filename: string;
  mime: string;
  bytes: number;
  width: number;
  height: number;
  has_icc_profile: boolean;
  icc_description: string | null;
  usage_status: UsageStatus;
  alt_text: string | null;
  created_at: string;
  urls: { work: string; thumb: string; original: string };
  /** set on an image cut from another one (Prepare images) */
  derived_from_id?: string | null;
  derivation?: unknown;
  created?: boolean;
}

export interface BrandLogo {
  asset_id: string;
  name: string;
  asset: Asset;
}

export interface Brand {
  id: string;
  name: string;
  spec: BrandSpec;
  /** the default logo (its row) */
  logo: Asset | null;
  logo_asset_id: string | null;
  logos: BrandLogo[];
  version: number;
  updated_at: string;
}

export interface Project {
  id: string;
  title: string;
  location: string | null;
  client_name: string | null;
  public_description: string | null;
  internal_notes: string | null;
  services: string[];
  confirmed_facts: string[];
  credits: string | null;
  language: string;
  publication_permission: Permission;
  embargo_until: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
  version: number;
  asset_count: number;
  item_count: number;
}

export interface Template {
  id: string;
  key: string;
  name: string;
  format: ContentFormat;
  description: string | null;
  layout: { artboards: { name?: string | null; background: string; layers: unknown[] }[] };
  is_builtin: boolean;
  revision: number;
  updated_at: string;
}

export type TemplateStatus = 'draft' | 'ready';
/** The Template Maker's shapes (app/api/content.py): `layout` is the published version, `editor_layout` the
 *  working version as pixels at the template's size. */
export interface TemplateRow extends Template {
  status: TemplateStatus;
  unpublished_changes: boolean;
  preset: 'square' | 'portrait' | 'vertical';
  /** the working version drawn with sample content (pixels); only when asked for */
  sample?: Layout;
}
export interface TemplateDetail extends TemplateRow {
  draft_revision: number;
  width: number;
  height: number;
  editor_layout: Layout;
}

export interface ItemRow {
  id: string;
  title: string;
  project_id: string | null;
  project_title?: string | null;
  template_id: string | null;
  format: ContentFormat;
  width: number;
  height: number;
  status: ContentStatus;
  published_url: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  revision_number: number | null;
}

export interface Warning {
  artboard: number;
  layer?: string;
  kind: 'low_resolution' | 'overflow' | 'missing_font' | 'missing_logo' | 'missing_image' | 'asset_usage' | 'outside';
  message: string;
  blocking: boolean;
}

export interface Revision {
  id: string;
  number: number;
  created_at: string;
  created_by: string | null;
  note: string | null;
  warnings: Warning[];
  layout?: Layout;
}

export interface ExportRow {
  id: string;
  revision_id: string;
  revision_number: number | null;
  file_format: 'png' | 'jpeg';
  files: { index: number; name: string; sha256: string; bytes: number; width: number; height: number; url: string; artboard: string; alt_text: string }[];
  package: { name: string; sha256: string; url: string };
  created_at: string;
}

export interface ItemDetail extends ItemRow {
  revision: (Revision & { layout: Layout }) | null;
  revisions: Revision[];
  approval: { id: string; revision_id: string; revision_number: number | null; at: string; by: string | null } | null;
  approval_history: { revision_number: number | null; at: string; by: string | null; revoked_at: string | null; revoked_reason: string | null }[];
  exports: ExportRow[];
  assets: Asset[];
  project: Project | null;
  brand: Brand;
}

export interface ContentMeta {
  presets: { key: 'square' | 'portrait' | 'vertical'; width: number; height: number }[];
  fonts: { family: string; licence: string }[];
  statuses: ContentStatus[];
  publication_permissions: Permission[];
  channels: string[];
  ai_drafts_available: boolean;
  max_asset_mb: number;
  /** what one request may carry through the production proxy */
  max_request_mb: number;
}

// Publications (issues 22-24), returned by app/api/publications.py.
export type PublicationKind = 'post' | 'carousel' | 'short' | 'reel' | 'story' | 'video' | 'article' | 'other';
export type PublicationStatus = 'planned' | 'in_progress' | 'ready' | 'published';
export type AccountKind = 'company' | 'personal';
export const CHANNELS = ['linkedin', 'instagram', 'facebook', 'youtube', 'tiktok', 'behance', 'x', 'website', 'other'] as const;

export interface PublishingAccount {
  id: string;
  label: string;
  channel: (typeof CHANNELS)[number];
  kind: AccountKind;
}

export interface PublicationRow {
  id: string;
  project_id: string;
  project_title: string | null;
  title: string;
  kind: PublicationKind;
  status: PublicationStatus;
  planned_for: string | null;
  output_folder: string | null;
  output_folder_is_link: boolean;
  notes: string | null;
  content_item_ids: string[];
  created_at: string;
  updated_at: string;
  version: number;
  text_count: number;
  file_count: number;
}

export interface PublicationText {
  id: string;
  account_id: string;
  text: string;
  hashtags: string[];
  language: string;
  published_url: string | null;
  published_at: string | null;
  position: number;
  version: number;
  updated_at: string;
}

export interface PublicationFile {
  id: string;
  sha256: string;
  filename: string;
  mime: string;
  bytes: number;
  kind: 'image' | 'video' | 'audio' | 'pdf';
  url: string;
  download_url: string;
  created?: boolean;
}

export interface TextSuggestion {
  id: string;
  account_id: string;
  language: string;
  text: string;
  hashtags: string[];
  warnings: string[];
  model: { provider?: string; model?: string | null; is_local?: boolean } | null;
  created_at: string;
}

export interface PublicationJob {
  id: string;
  account_id: string | null;
  status: string;
  error: string | null;
  created_at: string;
}

export interface PublicationDetail extends PublicationRow {
  project: { id: string; title: string; location?: string | null; language: string; publication_permission: Permission };
  texts: PublicationText[];
  files: PublicationFile[];
  suggestions: TextSuggestion[];
  project_items: { id: string; title: string; format: ContentFormat; status: ContentStatus }[];
  accounts: PublishingAccount[];
  jobs: PublicationJob[];
  model_jobs: { enabled: boolean; reason: string; agent_recently_seen: boolean };
}

export interface ToneOfVoice {
  description: string;
  do: string[];
  dont: string[];
  emoji: 'none' | 'sparing' | 'free';
  hashtags: { max: number; style: string };
  kind_notes: Record<AccountKind, string>;
  examples: { text: string; kind: AccountKind | null }[];
}

// --- Mirage Video links (docs/PLAN-mirage-video-integration.md) ---------------------------------

export type VideoFormat = '9:16' | '4:5' | '1:1' | '16:9';
export type VideoStatus = 'frames' | 'queued' | 'creating' | 'failed' | 'draft' | 'mockup' | 'approved' | 'film' | 'gone';
export type VideoOrigin = 'made' | 'motion' | 'linked';

export interface VideoFilm {
  render_id: string;
  sha256: string;
  bytes: number;
  file_id: string;
  format: string | null;
  width: number | null;
  height: number | null;
  duration_sec: number | null;
  credits: string[];
  mv_project_id: string;
  synced_at: string;
  url: string | null;
}

export interface VideoLink {
  id: string;
  project_id: string;
  publication_id: string;
  publication_title?: string | null;
  origin: VideoOrigin;
  mv_project_name: string | null;
  content_item_id: string | null;
  revision: number | null;
  frames: { expected: number; received: number } | null;
  style_notes: string[];
  skipped_films: number;
  format: VideoFormat;
  status: VideoStatus;
  error: string | null;
  shots: number;
  mv_project_id: string | null;
  storyboard: { status: string | null; locked: boolean | null; duration_sec: number | null; item_count: number | null; credits: string[] } | null;
  renders: { mockups: number; films: number };
  films: VideoFilm[];
  last_synced_at: string | null;
  created_at: string;
  job_id: string | null;
}

export interface VideoDefaults {
  format: VideoFormat;
  movement: string;
  intensity: string;
  pace: string;
  duration_sec: number;
  transition: string;
}

export interface VideoMeta {
  configured: boolean;
  reason: string | null;
  public_url: string | null;
  can_make: boolean;
  defaults: VideoDefaults;
}

export interface VideoOptions {
  formats: VideoFormat[];
  movements: { key: string; label: string }[];
  intensities: string[];
  paces: string[];
  transitions: string[];
  text_reveals: string[];
  style_notes: string[];
  music: { id: string; label: string | null; original_name: string; category: string | null; duration_sec: number | null; credit: string | null }[];
  logos: { asset_id: string; name: string }[];
  default_logo_id: string | null;
  brand_name: string;
  defaults: VideoDefaults;
}

export interface MirageVideoProject {
  id: string;
  name: string;
  location: string | null;
  format: string | null;
  archived: boolean;
  updated_at: string | null;
  final_renders: number;
  last_final_at: string | null;
  made_here: boolean;
  links_here: number;
}

export interface MotionStart {
  video: VideoLink;
  created: boolean;
  canvas: [number, number];
  slides: { artboard_id: string; background: string; moving_layers: string[] }[];
  revision_id: string;
}
